import * as React from 'react';
import styled from 'styled-components';
import AnimatedInput from '../../components/AnimatedInput/AnimatedInput';
import SLink from '../../components/StyledLink/styledLink';
import { OnlineForm, ActionButton, FormField, FormValue, HelpText } from '../../components/onlineForm/onlineForm';
import Top from '../DevelopersTop/DevelopersTop';
import { IFieldChangeData, IFieldData, signInPopupType, IPopupData } from '../../models/developerPortal';
import ResetPasswordPopup from '../../components/newPopup/resetPasswordPopup';
import Subtitle from '../../components/onlineSubtitle/onlineSubtitle';
import Title from '../../components/onlineTitle/Title';
import logo from '../../assets/sunbit-logo-blue.svg';
import addEventKeyUpListenerHelper from '../../helpers/listenerHelper';
import { PopupTheme } from '../../enums/common';
import { MessagePopup } from '../../components/newPopup/messagePopup';

// ############################ Styles ###########################

const SContentWrapper = styled.div(
  ({ theme }) => `
  height: 100%;
  background: ${theme.colors.lightBackground};
  @media only screen and ${theme.device.laptop} {
    width: 100%;
  }
`
);

const SLogo = styled.img(
  ({ theme }) => `
  width: 140px;
  height: auto;
  margin: auto;
  margin-bottom: 40px;
`
);

// ############################ End Styles ###########################

export interface ISignInProps {
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
  ratesAndTermsUrl: string;
  username: IFieldData | string;
  password: IFieldData;
  confirmPassword: IFieldData;
  isSignedUp: boolean;
  handleLogin: (userName: string, password: string) => void;
  handleRegistration: (password: string) => void;
  onChange: (fieldChangeData: IFieldChangeData) => void;
  resetPassword: () => void;
  setResetPasswordPopupStatus: (currentStatus: boolean) => void;
  resetPasswordPopup: boolean;
  resetPopupField: IFieldData;
  signInPopupData: IPopupData;
  setSignInPopup: (popupStatus: string) => void;
}

const SignIn: React.FC<ISignInProps> = (props) => {
  const {
    privacyPolicyUrl,
    termsOfUseUrl,
    ratesAndTermsUrl,
    username,
    password,
    confirmPassword,
    resetPopupField,
    signInPopupData,
    setSignInPopup,
    isSignedUp,
    handleLogin,
    handleRegistration,
    onChange,
    resetPassword,
    setResetPasswordPopupStatus,
    resetPasswordPopup,
  } = props;
  const [disabled, setDisabled] = React.useState(true);

  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ value: e.target.value, fieldName: e.target.name });
  };

  const isDisabled = () => {
    let allFieldsAreSet: boolean;
    let allFieldsNoErrors: boolean;
    if (isSignedUp) {
      allFieldsAreSet = Boolean(typeof username !== 'string' ? username.value : '') && Boolean(password.value);
      if (typeof username !== 'string') {
        allFieldsNoErrors = !username.error && !password.error;
      }
    } else {
      allFieldsAreSet = Boolean(password.value) && Boolean(confirmPassword.value);
      allFieldsNoErrors = !password.error && !confirmPassword.error;
    }

    const isDisabled: boolean = !allFieldsNoErrors || !allFieldsAreSet;
    setDisabled(isDisabled);
  };

  const mailTo = () => {
    window.open('mailto:Integration@sunbit.com');
  };

  const onSubmitEnterKeyUp = () => {
    if (!disabled) {
      if (isSignedUp) {
        handleLogin(typeof username !== 'string' ? username.value : '', password.value);
      } else {
        handleRegistration(password.value);
      }
    }
  };

  const handleWebhooksPopupState = () => {
    signInPopupData.status === '' ? setSignInPopup('signInFail') : setSignInPopup('');
  };

  React.useEffect(() => {
    isDisabled();
  }, [username, password, confirmPassword]);

  React.useEffect(() => {
    addEventKeyUpListenerHelper('onlineForm', onSubmitEnterKeyUp);
  }, []);

  return (
    <SContentWrapper data-at="contentWrapper">
      <Top
        privacyPolicyUrl={privacyPolicyUrl}
        termsOfUseUrl={termsOfUseUrl}
        ratesAndTermsUrl={ratesAndTermsUrl}
        showProfileThumb={true}
        showDeveloperDocuments={true}
        headerTitleText=""
        username=""
      />
      <OnlineForm>
        <>
          <SLogo src={logo} alt="sunbit logo"></SLogo>
          <Title>
            Welcome to the Sunbit <br /> Developer Portal
          </Title>{' '}
          <br />
          {isSignedUp ? '' : <Subtitle alignLeft={false}>Please choose a password to activate your account.</Subtitle>}
          {isSignedUp ? (
            <FormField data-at="username" className="with-error">
              <label>Username</label>
              <AnimatedInput
                onKeyEnter={onSubmitEnterKeyUp}
                animatedLabel=""
                onChange={(e) => onChange({ fieldName: 'userName', value: e.target.value })}
                value={typeof username !== 'string' ? username.value : username}
                error={typeof username !== 'string' ? username.error : ''}
              />
            </FormField>
          ) : (
            <>
              <FormField data-at="username" className="with-error">
                <label>Username</label>
                <FormValue>{username}</FormValue>
              </FormField>
            </>
          )}
          <FormField data-at="password" className="with-error">
            <label>Password</label>
            <AnimatedInput
              onKeyEnter={onSubmitEnterKeyUp}
              isSecuredInput
              animatedLabel=""
              onChange={(e) => onChange({ fieldName: 'password', value: e.target.value })}
              value={password.value}
              error={password.error}
            />
          </FormField>
          {isSignedUp ? (
            <>
              <ActionButton
                dataAt="submitAndLogin"
                id="submitAndLogin"
                buttonText="Login"
                submit={() => handleLogin(typeof username !== 'string' ? username.value : '', password.value)}
                disabled={disabled}
              />
              <HelpText>
                <SLink
                  onClick={() => {
                    setResetPasswordPopupStatus(resetPasswordPopup);
                  }}
                >
                  Forgot password?
                </SLink>
              </HelpText>
              <br />
              <br />
            </>
          ) : (
            <>
              <FormField data-at="re-type-password" className="with-error">
                <label>Re-type Password:</label>
                <AnimatedInput
                  onKeyEnter={onSubmitEnterKeyUp}
                  isSecuredInput
                  animatedLabel=""
                  onChange={(e) => onChange({ fieldName: 'confirmPassword', value: e.target.value })}
                  value={confirmPassword.value}
                  error={confirmPassword.error}
                />
              </FormField>
              <ActionButton
                dataAt="submitAndLogin"
                id="submitAndLogin"
                buttonText="Activate"
                submit={() => handleRegistration(password.value)}
                disabled={disabled}
              />
            </>
          )}
          <HelpText>
            Need help? Contact us at <SLink onClick={mailTo}>Integration@sunbit.com</SLink>
          </HelpText>
        </>
      </OnlineForm>
      {resetPasswordPopup && (
        <ResetPasswordPopup
          dataAt={'resetPasswordPopup'}
          boldText={'Forgot Password'}
          fieldData={resetPopupField}
          fieldLabel={'User Name'}
          resetPasswordPopup={resetPasswordPopup}
          setPopupStatus={setResetPasswordPopupStatus}
          resetPassword={resetPassword}
          handleUserChange={handleUserChange}
        />
      )}
      {signInPopupData.status === signInPopupType.signInFail && (
        <MessagePopup
          dataAt={'addMemberFailPopup'}
          boldText={''}
          blueButtonText={'Close'}
          blueButtonAction={handleWebhooksPopupState}
          messagePopupData={{
            title: signInPopupData.title,
            description: signInPopupData.description,
            status: PopupTheme.danger,
          }}
        />
      )}
      {signInPopupData.status === signInPopupType.resetPasswordSuccess && (
        <MessagePopup
          dataAt={'addMemberSuccessPopup'}
          boldText={''}
          blueButtonText={'Close'}
          blueButtonAction={handleWebhooksPopupState}
          messagePopupData={{
            title: signInPopupData.title,
            description: signInPopupData.description,
            status: PopupTheme.success,
          }}
        />
      )}
    </SContentWrapper>
  );
};

export default SignIn;
